import { useState, useCallback, useEffect } from 'react';
import { adaptiveApi } from '../services/adaptiveApi';

/**
 * Hook for managing the review queue with optimistic updates.
 *
 * @param {{ enabled?: boolean, limit?: number }} options
 * @returns {{
 *   items: Array<{
 *     conceptId: string,
 *     courseId: string | null,
 *     conceptName: string | null,
 *     retrievability: number,
 *     status: string,
 *     priority: number,
 *     lastReviewAt: string | null,
 *     nextReviewAt: string | null
 *   }>,
 *   totalDue: number,
 *   isLoading: boolean,
 *   isSubmitting: boolean,
 *   error: string | null,
 *   submitGrade: (conceptId: string, grade: 'AGAIN' | 'HARD' | 'GOOD' | 'EASY') => Promise<{ nextReviewAt: string }>,
 *   refetch: () => Promise<void>
 * }}
 */
export function useReviewQueue({ enabled = true, limit = 20 } = {}) {
  const [items, setItems] = useState([]);
  const [totalDue, setTotalDue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fetchQueue = useCallback(async () => {
    if (!enabled) {
      setItems([]);
      setTotalDue(0);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await adaptiveApi.getReviewQueue({ limit, auth: true });
      setItems(response.items || []);
      setTotalDue(response.totalDue || 0);
    } catch (err) {
      console.error('[useReviewQueue] Failed to fetch queue:', err);
      setError(err?.message || 'Failed to fetch review queue');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, limit]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const submitGrade = useCallback(async (conceptId, grade) => {
    // Capture the item being removed for potential rollback
    let removedItem = null;

    try {
      setIsSubmitting(true);
      setError(null);

      // Optimistic update: remove item immediately, capturing it for rollback
      setItems(prev => {
        removedItem = prev.find(item => item.conceptId === conceptId);
        return prev.filter(item => item.conceptId !== conceptId);
      });
      setTotalDue(prev => Math.max(0, prev - 1));

      const response = await adaptiveApi.submitReview({
        conceptId,
        grade,
        auth: true,
      });

      return response;
    } catch (err) {
      console.error('[useReviewQueue] Failed to submit grade:', err);
      // Rollback on error: re-add the removed item
      if (removedItem) {
        setItems(prev => [...prev, removedItem]);
        setTotalDue(prev => prev + 1);
      }
      setError(err?.message || 'Failed to submit review');
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return {
    items,
    totalDue,
    isLoading,
    isSubmitting,
    error,
    submitGrade,
    refetch: fetchQueue,
  };
}
